import { useEffect, useMemo, useState, type ReactNode } from 'react';
import { ArrowDown, ArrowUp, ArrowUpDown, ChevronLeft, ChevronRight } from 'lucide-react';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from 'src/components/ui/table';
import { portalDs } from 'src/components/portal-design-system';
import { cn } from 'src/lib/utils';

export type DataTableColumn<T> = {
  id: string;
  header: string;
  accessor: (row: T) => string | number | null | undefined;
  render?: (row: T) => ReactNode;
  sortable?: boolean;
  align?: 'left' | 'right';
};

export type DataTableSortState = {
  columnId: string;
  direction: 'asc' | 'desc';
};

export interface DataTableProps<T> {
  rows: T[];
  columns: DataTableColumn<T>[];
  emptyMessage?: string;
  pageSize?: number;
  getRowKey: (row: T, index: number) => string;
  defaultSort?: DataTableSortState | null;
  serverPagination?: {
    page: number;
    pageSize: number;
    totalCount: number;
    onPageChange: (page: number) => void;
  };
  onRowClick?: (row: T) => void;
}

function compareValues(
  left: string | number | null | undefined,
  right: string | number | null | undefined
): number {
  if (left === right) {
    return 0;
  }
  if (left === null || left === undefined || left === '') {
    return 1;
  }
  if (right === null || right === undefined || right === '') {
    return -1;
  }
  if (typeof left === 'number' && typeof right === 'number') {
    return left - right;
  }
  return String(left).localeCompare(String(right));
}

export function DataTable<T>({
  rows,
  columns,
  emptyMessage = 'No rows match the current filters.',
  pageSize = 10,
  getRowKey,
  defaultSort = null,
  serverPagination,
  onRowClick,
}: DataTableProps<T>) {
  const [sort, setSort] = useState<DataTableSortState | null>(defaultSort);
  const [page, setPage] = useState(1);

  useEffect(() => {
    setPage(1);
  }, [rows]);

  const sortedRows = useMemo(() => {
    if (serverPagination || !sort) {
      return rows;
    }
    const column = columns.find((entry) => entry.id === sort.columnId);
    if (!column) {
      return rows;
    }
    return [...rows].sort((left, right) => {
      const result = compareValues(column.accessor(left), column.accessor(right));
      return sort.direction === 'asc' ? result : -result;
    });
  }, [columns, rows, serverPagination, sort]);

  const activePage = serverPagination?.page ?? page;
  const activePageSize = serverPagination?.pageSize ?? pageSize;
  const totalCount = serverPagination?.totalCount ?? sortedRows.length;
  const totalPages = Math.max(1, Math.ceil(totalCount / activePageSize));
  const safePage = Math.min(activePage, totalPages);

  const paginatedRows = useMemo(() => {
    if (serverPagination) {
      return rows;
    }
    const start = (safePage - 1) * activePageSize;
    return sortedRows.slice(start, start + activePageSize);
  }, [activePageSize, rows, safePage, serverPagination, sortedRows]);

  const toggleSort = (columnId: string) => {
    if (serverPagination) {
      return;
    }
    setPage(1);
    setSort((current) => {
      if (!current || current.columnId !== columnId) {
        return { columnId, direction: 'asc' };
      }
      if (current.direction === 'asc') {
        return { columnId, direction: 'desc' };
      }
      return null;
    });
  };

  const start = totalCount === 0 ? 0 : (safePage - 1) * activePageSize + 1;
  const end = Math.min(safePage * activePageSize, totalCount);
  const showPagination = serverPagination ? totalCount > activePageSize : sortedRows.length > activePageSize;

  const handlePageChange = (nextPage: number) => {
    if (serverPagination) {
      serverPagination.onPageChange(nextPage);
      return;
    }
    setPage(nextPage);
  };

  return (
    <div className="flex flex-col gap-3">
      <div className={portalDs.tableContainer}>
        <Table>
          <TableHeader>
            <TableRow className={portalDs.tableHead}>
              {columns.map((column) => {
                const isActive = sort?.columnId === column.id;
                const SortIcon = !column.sortable
                  ? null
                  : !isActive
                    ? ArrowUpDown
                    : sort?.direction === 'asc'
                      ? ArrowUp
                      : ArrowDown;

                return (
                  <TableHead
                    key={column.id}
                    className={cn(
                      portalDs.tableHeadCell,
                      column.align === 'right' && 'text-right'
                    )}
                  >
                    {column.sortable ? (
                      <button
                        type="button"
                        className={cn(
                          'inline-flex items-center gap-1',
                          portalDs.tableHeadCellSortable,
                          isActive && 'text-[#5E6278]'
                        )}
                        onClick={() => toggleSort(column.id)}
                      >
                        {column.header}
                        {SortIcon ? <SortIcon className="size-3.5" /> : null}
                      </button>
                    ) : (
                      column.header
                    )}
                  </TableHead>
                );
              })}
            </TableRow>
          </TableHeader>
          <TableBody>
            {paginatedRows.length === 0 ? (
              <TableRow>
                <TableCell
                  colSpan={columns.length}
                  className={cn(portalDs.tableCell, 'py-10 text-center', portalDs.tableCellMuted)}
                >
                  {emptyMessage}
                </TableCell>
              </TableRow>
            ) : (
              paginatedRows.map((row, index) => (
                <TableRow
                  key={getRowKey(row, index)}
                  className={cn(
                    portalDs.tableBodyRow,
                    onRowClick && 'cursor-pointer hover:bg-[#F7F8FA]'
                  )}
                  onClick={onRowClick ? () => onRowClick(row) : undefined}
                >
                  {columns.map((column) => (
                    <TableCell
                      key={column.id}
                      className={cn(
                        portalDs.tableCell,
                        column.align === 'right' && cn('text-right', portalDs.tableCellMono)
                      )}
                    >
                      {column.render ? column.render(row) : column.accessor(row) ?? '—'}
                    </TableCell>
                  ))}
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </div>

      <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
        <p className={portalDs.rowCount}>
          {totalCount === 0
            ? 'No results'
            : `Showing ${start}–${end} of ${totalCount}`}
        </p>

        {showPagination ? (
          <div className="flex items-center justify-center gap-1">
            <button
              type="button"
              className={cn(
                'flex size-[34px] items-center justify-center rounded-lg border-[1.5px] border-[#D9DBE3] bg-white text-[#5E6278] transition-colors hover:border-[#B8BBCA] hover:text-[#0D0F1A] disabled:cursor-not-allowed disabled:opacity-40'
              )}
              disabled={safePage <= 1}
              onClick={() => handlePageChange(Math.max(1, safePage - 1))}
              aria-label="Previous page"
            >
              <ChevronLeft className="size-4" />
            </button>

            {Array.from({ length: totalPages }, (_, index) => index + 1).map((pageNumber) => (
              <button
                key={pageNumber}
                type="button"
                className={cn(
                  'flex size-[34px] items-center justify-center rounded-lg border-[1.5px] text-[13px] font-semibold transition-colors',
                  pageNumber === safePage
                    ? 'border-[#0D0F1A] bg-[#0D0F1A] text-white'
                    : 'border-[#D9DBE3] bg-white text-[#5E6278] hover:border-[#B8BBCA] hover:text-[#0D0F1A]'
                )}
                onClick={() => handlePageChange(pageNumber)}
                aria-current={pageNumber === safePage ? 'page' : undefined}
              >
                {pageNumber}
              </button>
            ))}

            <button
              type="button"
              className={cn(
                'flex size-[34px] items-center justify-center rounded-lg border-[1.5px] border-[#D9DBE3] bg-white text-[#5E6278] transition-colors hover:border-[#B8BBCA] hover:text-[#0D0F1A] disabled:cursor-not-allowed disabled:opacity-40'
              )}
              disabled={safePage >= totalPages}
              onClick={() => handlePageChange(Math.min(totalPages, safePage + 1))}
              aria-label="Next page"
            >
              <ChevronRight className="size-4" />
            </button>
          </div>
        ) : null}
      </div>
    </div>
  );
}
